/**
 * Зарах үнийн өөрчлөлт (`/accounting/price-changes`) — өдөр, сар, оноор.
 *
 * Салбар = үнэ өөрчлөгдөх МӨЧИД гутал байсан газар. Анхны үнэ (эхний
 * үлдэгдэл, үнэгүй байсан гутлын 0₮ → үнэ) өөрчлөлтөд орохгүй.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDate, formatDateTime, formatMnt, formatSerialDisplay, formatSize, money } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { DataTable } from '../data-table';
import { Section } from '../section';
import { Empty, ErrorNote, Tabs } from '../ui';
import { AccountingFilters, ExcelButton, saveTableXlsx, useAccountingFilter } from './shared';

type GroupBy = 'day' | 'month' | 'year';

interface Group { key: string; changes: number; up: number; down: number; deltaMnt: string }
interface Change {
  id: string;
  changedAt: string;
  serial: string | null;
  size: number | null;
  oldPrice: string;
  newPrice: string;
  deltaMnt: string;
  reason: string | null;
  changedBy: string;
  store: { id: string; name: string } | null;
}

const GROUP_TABS: { key: GroupBy; label: string }[] = [
  { key: 'day', label: 'Өдрөөр' },
  { key: 'month', label: 'Сараар' },
  { key: 'year', label: 'Оноор' },
];

const signed = (v: string) => {
  const d = money(v);
  if (d.isZero()) return '0₮';
  return <span className={d.gt(0) ? 'text-accent-700' : 'text-alert-ink'}>{d.gt(0) ? '+' : '−'}{formatMnt(d.abs())}</span>;
};

export function PriceChangesTab() {
  const f = useAccountingFilter();
  const [groupBy, setGroupBy] = useState<GroupBy>('day');
  const query = f.query({ groupBy });
  const q = useQuery({
    queryKey: ['accounting', 'price-changes', query],
    queryFn: () => api<Envelope<Group[]> & { meta: { items: Change[] } }>(`/accounting/price-changes?${query}`),
  });
  const groups = q.data?.data ?? [];
  const items = q.data?.meta.items ?? [];

  const excel = () =>
    saveTableXlsx({
      file: `une-oorchlolt-${f.from}-${f.to}.xlsx`,
      title: 'Зарах үнийн өөрчлөлт',
      period: `${f.from} — ${f.to}`,
      headers: ['Огноо', 'Салбар', 'Баркод', 'Размер', 'Хуучин ₮', 'Шинэ ₮', 'Зөрүү ₮', 'Хэн', 'Шалтгаан'],
      widths: [18, 20, 15, 8, 14, 14, 14, 20, 24],
      rows: items.map((c) => [
        formatDateTime(c.changedAt), c.store?.name ?? '—', c.serial ?? '—', formatSize(c.size),
        { money: c.oldPrice }, { money: c.newPrice }, { money: c.deltaMnt }, c.changedBy, c.reason ?? '',
      ]),
    });

  return (
    <div className="flex flex-col gap-3.5">
      <Section
        title="Зарах үнийн өөрчлөлт"
        note="Салбар нь үнэ өөрчлөгдөх үед гутал байсан газар. Анхны үнэ тавилт тоологдохгүй."
        actions={<ExcelButton onClick={excel} disabled={items.length === 0} />}
      >
        <AccountingFilters f={f} />
        <Tabs tabs={GROUP_TABS} value={groupBy} onChange={setGroupBy} />
        {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
        <DataTable
          rows={groups}
          loading={q.isPending}
          rowKey={(g) => g.key}
          empty={<Empty>Энэ хугацаанд үнэ өөрчлөгдөөгүй</Empty>}
          columns={[
            {
              key: 'key', header: groupBy === 'day' ? 'Өдөр' : groupBy === 'month' ? 'Сар' : 'Он', nowrap: true,
              mobile: 'title', cell: (g) => (groupBy === 'day' ? formatDate(g.key) : g.key),
            },
            { key: 'changes', header: 'Өөрчлөлт', align: 'right', cell: (g) => g.changes },
            { key: 'up', header: 'Өссөн', align: 'right', cell: (g) => g.up },
            { key: 'down', header: 'Буурсан', align: 'right', cell: (g) => g.down },
            { key: 'delta', header: 'Нийт зөрүү', align: 'right', nowrap: true, mobile: 'value', cell: (g) => signed(g.deltaMnt) },
          ]}
        />
      </Section>

      {items.length > 0 ? (
        <Section title="Дэлгэрэнгүй" subtitle={`${items.length} өөрчлөлт`}>
          <DataTable
            rows={items}
            rowKey={(c) => c.id}
            sticky
            columns={[
              { key: 'at', header: 'Огноо', nowrap: true, cell: (c) => formatDateTime(c.changedAt) },
              { key: 'store', header: 'Салбар', cell: (c) => c.store?.name ?? '—' },
              { key: 'serial', header: 'Баркод', nowrap: true, mobile: 'title', cell: (c) => <span className="num">{formatSerialDisplay(c.serial)}</span> },
              { key: 'size', header: 'Размер', align: 'right', cell: (c) => formatSize(c.size) },
              { key: 'old', header: 'Хуучин', align: 'right', nowrap: true, cell: (c) => formatMnt(c.oldPrice) },
              { key: 'new', header: 'Шинэ', align: 'right', nowrap: true, cell: (c) => formatMnt(c.newPrice) },
              { key: 'delta', header: 'Зөрүү', align: 'right', nowrap: true, mobile: 'value', cell: (c) => signed(c.deltaMnt) },
              { key: 'by', header: 'Хэн', cell: (c) => c.changedBy },
              { key: 'reason', header: 'Шалтгаан', cell: (c) => c.reason ?? '—' },
            ]}
          />
        </Section>
      ) : null}
    </div>
  );
}
